import React, { useState } from 'react';
import { Loader2, Check, X, Mail, ArrowRight, Gift } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { ProductDetails, GrowingEnvironment, SeedPackageType } from '../../pages/landing/constants';

interface EmailCaptureFormProps {
  product: ProductDetails;
  sourcePage: string;
  onSuccess?: () => void;
  growingEnvironment?: GrowingEnvironment;
  seedPackage?: SeedPackageType;
  quizAnswers?: Record<string, string>;
}

export default function EmailCaptureForm({
  product,
  sourcePage,
  onSuccess,
  growingEnvironment,
  seedPackage,
  quizAnswers
}: EmailCaptureFormProps) {
  const { user } = useAuth();
  const [emailInput, setEmailInput] = useState(user?.email || '');
  const [isGiftPurchase, setIsGiftPurchase] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientZipCode, setRecipientZipCode] = useState('');
  const [shippingToRecipient, setShippingToRecipient] = useState<boolean | null>(null);
  const [notifyRecipient, setNotifyRecipient] = useState(false);
  const [streetAddress, setStreetAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!emailInput.trim()) {
      setError('Please enter your email address.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (isGiftPurchase) {
        if (!recipientEmail.trim() || !/\S+@\S+\.\S+/.test(recipientEmail)) {
          setError('Please enter a valid recipient email address.');
          setLoading(false);
          return;
        }
        if (!recipientZipCode.trim() || recipientZipCode.length !== 5) {
          setError('Please enter a valid 5-digit zip code for the recipient.');
          setLoading(false);
          return;
        }
        if (shippingToRecipient === null) {
          setError('Please select where to ship this gift.');
          setLoading(false);
          return;
        }
        if (shippingToRecipient) {
          if (!streetAddress.trim() || !city.trim() || !state) {
            setError('Please complete the shipping address.');
            setLoading(false);
            return;
          }
        }
      }

      const submissionData: any = {
        email: emailInput.toLowerCase(),
        box_theme: seedPackage || 'unknown',
        growing_environment: growingEnvironment,
        source_page: sourcePage,
        is_gift_purchase: isGiftPurchase,
        quiz_answers: quizAnswers ? JSON.stringify(quizAnswers) : null
      };

      if (isGiftPurchase) {
        submissionData.recipient_email = recipientEmail.toLowerCase();
        submissionData.recipient_zip_code = recipientZipCode;
        submissionData.shipping_to_recipient = shippingToRecipient;
        submissionData.notify_recipient_via_email = notifyRecipient;

        if (shippingToRecipient) {
          submissionData.street_address = streetAddress;
          submissionData.city = city;
          submissionData.state = state;
        }
      }

      const { error: submissionError } = await supabase
        .from('grow_box_submissions')
        .insert(submissionData);

      if (submissionError) throw submissionError;

      // Subscribe to newsletter if not logged in
      if (!user) {
        const response = await fetch('/.netlify/functions/subscribe-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: emailInput.toLowerCase(),
            tags: ['Website', 'Grow Box Purchase'],
            source: 'Grow Box Checkout'
          })
        });

        if (!response.ok) {
          console.error('Failed to subscribe to newsletter');
        }
      }

      setSuccess(true);

      setTimeout(() => {
        if (product?.external_url) {
          const url = new URL(product.external_url);
          if (growingEnvironment) url.searchParams.set('environment', growingEnvironment);
          if (seedPackage) url.searchParams.set('seed_package', seedPackage);
          window.location.href = url.toString();
        }
        onSuccess?.();
      }, 1500);

    } catch (err) {
      console.error('Error submitting email:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="text-center">
        <div className="flex items-center justify-center gap-2 text-green-600 mb-4">
          <Check className="h-6 w-6" />
          <p className="font-medium">Success!</p>
        </div>
        <p className="text-gray-600 mb-4">
          Hang tight! We're redirecting you to the checkout page
        </p>
        <p className="text-sm text-gray-500">
          Redirecting you to get started...
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="flex items-center gap-2 p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <input
          type="checkbox"
          id="isGift"
          checked={isGiftPurchase}
          onChange={(e) => setIsGiftPurchase(e.target.checked)}
          className="h-5 w-5 text-primary-500 border-gray-300 rounded focus:ring-primary-500"
        />
        <label htmlFor="isGift" className="flex items-center gap-2 text-gray-800 font-medium cursor-pointer">
          <Gift className="h-5 w-5 text-primary-500" />
          <span>This is a gift</span>
        </label>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {isGiftPurchase ? 'Your Email (Purchaser)' : 'Your Email'}
        </label>
        <div className="relative">
          <input
            type="email"
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            placeholder="Enter your email address"
            className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
            required
          />
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
        </div>
        <p className="text-xs text-gray-500 mt-1">
          We'll send order updates here
        </p>
      </div>

      {isGiftPurchase && (
        <div className="space-y-6 p-4 bg-green-50 border-2 border-green-200 rounded-lg">
          <h4 className="font-semibold text-gray-900 flex items-center gap-2">
            <Gift className="h-5 w-5 text-green-600" />
            Gift Recipient Information
          </h4>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Recipient's Email Address
            </label>
            <div className="relative">
              <input
                type="email"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                placeholder="recipient@example.com"
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                required
              />
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Recipient's Zip Code
            </label>
            <input
              type="text"
              maxLength={5}
              pattern="[0-9]*"
              value={recipientZipCode}
              onChange={(e) => setRecipientZipCode(e.target.value.replace(/\D/g, ''))}
              placeholder="12345"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Needed for zone-specific gardening plan
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Where should we ship this gift?
            </label>
            <div className="space-y-2">
              <label className="flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer transition-colors hover:bg-white border-gray-300 hover:border-primary-300">
                <input
                  type="radio"
                  name="shippingDestination"
                  checked={shippingToRecipient === false}
                  onChange={() => setShippingToRecipient(false)}
                  className="mt-0.5 h-4 w-4 text-primary-500 border-gray-300 focus:ring-primary-500"
                  required
                />
                <div className="flex-1">
                  <div className="font-medium text-gray-900">Ship to me</div>
                  <div className="text-sm text-gray-600">I'll give it to them in person</div>
                </div>
              </label>
              <label className="flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer transition-colors hover:bg-white border-gray-300 hover:border-primary-300">
                <input
                  type="radio"
                  name="shippingDestination"
                  checked={shippingToRecipient === true}
                  onChange={() => setShippingToRecipient(true)}
                  className="mt-0.5 h-4 w-4 text-primary-500 border-gray-300 focus:ring-primary-500"
                  required
                />
                <div className="flex-1">
                  <div className="font-medium text-gray-900">Ship directly to recipient</div>
                  <div className="text-sm text-gray-600">Send it straight to them</div>
                </div>
              </label>
            </div>
          </div>

          {shippingToRecipient && (
            <div className="space-y-4 pt-4 border-t border-green-300">
              <h5 className="font-medium text-gray-900">Recipient's Shipping Address</h5>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Street Address
                </label>
                <input
                  type="text"
                  value={streetAddress}
                  onChange={(e) => setStreetAddress(e.target.value)}
                  placeholder="123 Main St"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    City
                  </label>
                  <input
                    type="text"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="City"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    State
                  </label>
                  <select
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                    required
                  >
                    <option value="">Select</option>
                    <option value="AL">AL</option>
                    <option value="AK">AK</option>
                    <option value="AZ">AZ</option>
                    <option value="AR">AR</option>
                    <option value="CA">CA</option>
                    <option value="CO">CO</option>
                    <option value="CT">CT</option>
                    <option value="DE">DE</option>
                    <option value="FL">FL</option>
                    <option value="GA">GA</option>
                    <option value="HI">HI</option>
                    <option value="ID">ID</option>
                    <option value="IL">IL</option>
                    <option value="IN">IN</option>
                    <option value="IA">IA</option>
                    <option value="KS">KS</option>
                    <option value="KY">KY</option>
                    <option value="LA">LA</option>
                    <option value="ME">ME</option>
                    <option value="MD">MD</option>
                    <option value="MA">MA</option>
                    <option value="MI">MI</option>
                    <option value="MN">MN</option>
                    <option value="MS">MS</option>
                    <option value="MO">MO</option>
                    <option value="MT">MT</option>
                    <option value="NE">NE</option>
                    <option value="NV">NV</option>
                    <option value="NH">NH</option>
                    <option value="NJ">NJ</option>
                    <option value="NM">NM</option>
                    <option value="NY">NY</option>
                    <option value="NC">NC</option>
                    <option value="ND">ND</option>
                    <option value="OH">OH</option>
                    <option value="OK">OK</option>
                    <option value="OR">OR</option>
                    <option value="PA">PA</option>
                    <option value="RI">RI</option>
                    <option value="SC">SC</option>
                    <option value="SD">SD</option>
                    <option value="TN">TN</option>
                    <option value="TX">TX</option>
                    <option value="UT">UT</option>
                    <option value="VT">VT</option>
                    <option value="VA">VA</option>
                    <option value="WA">WA</option>
                    <option value="WV">WV</option>
                    <option value="WI">WI</option>
                    <option value="WY">WY</option>
                  </select>
                </div>
              </div>

              <div className="flex items-start gap-2 mt-4">
                <input
                  type="checkbox"
                  id="notifyRecipient"
                  checked={notifyRecipient}
                  onChange={(e) => setNotifyRecipient(e.target.checked)}
                  className="mt-1 h-4 w-4 text-primary-500 border-gray-300 rounded focus:ring-primary-500"
                />
                <label htmlFor="notifyRecipient" className="text-sm text-gray-700 cursor-pointer">
                  Notify recipient via email about this gift
                  <span className="block text-xs text-gray-500 mt-1">
                    We'll send them an email letting them know a gift is on the way
                  </span>
                </label>
              </div>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <X className="h-5 w-5" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      <button
        type="submit"
        disabled={loading || !emailInput.trim()}
        className="w-full bg-primary-500 text-white py-3 px-4 rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-semibold"
      >
        {loading ? (
          <Loader2 className="h-5 w-5 animate-spin" />
        ) : (
          <>
            <span>Complete Your Order</span>
            <ArrowRight className="h-5 w-5" />
          </>
        )}
      </button>

      <p className="text-xs text-gray-500 text-center">
        By continuing, you agree to receive updates about your personalized garden plan and growing tips.
      </p>
    </form>
  );
}
